// src/pages/AdminElibrary.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { db } from "../firebase";
import { ref, onValue, push, remove, update, set } from "firebase/database";

export default function AdminElibrary() {
  const navigate = useNavigate();
  const location = useLocation();
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    icon: "📁"
  });
  const [resourceForm, setResourceForm] = useState({
    title: "",
    description: "",
    type: "link",
    url: ""
  });
  const [message, setMessage] = useState({ type: "", text: "" });

  const role = localStorage.getItem("role");
  const userName = localStorage.getItem("userName") || "Admin";

  // Bottom navigation items for admin
  const bottomNavItems = [
    { id: "nav_dashboard", name: "Dashboard", path: "/admin-dashboard", icon: "🏠", activeIcon: "🏠" },
    { id: "nav_books", name: "Books", path: "/books", icon: "📚", activeIcon: "📚" },
    { id: "nav_teachers", name: "Teachers", path: "/teachers", icon: "👩‍🏫", activeIcon: "👩‍🏫" },
    { id: "nav_elibrary", name: "E-Library", path: "/admin-elibrary", icon: "📖", activeIcon: "📖" },
    { id: "nav_reports", name: "Reports", path: "/reports", icon: "📊", activeIcon: "📊" },
    { id: "nav_settings", name: "Settings", path: "/settings", icon: "⚙️", activeIcon: "⚙️" }
  ];

  // Fetch folders
  useEffect(() => {
    const foldersRef = ref(db, "elibrary_folders");
    const unsubscribe = onValue(foldersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const folderList = Object.entries(data).map(([id, folder]) => ({ id, ...folder }));
        setFolders(folderList);
      } else {
        setFolders([]);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Fetch resources when folder changes
  useEffect(() => {
    if (!selectedFolder) {
      setResources([]);
      return;
    }
    
    const resourcesRef = ref(db, `elibrary_resources/${selectedFolder.id}`);
    const unsubscribe = onValue(resourcesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const resourceList = Object.entries(data).map(([id, resource]) => ({ id, ...resource }));
        setResources(resourceList);
      } else {
        setResources([]);
      }
    });
    return () => unsubscribe();
  }, [selectedFolder]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), 3000);
  };

  // Folder CRUD operations
  const createFolder = async () => {
    if (!formData.name.trim()) {
      showMessage("error", "Please enter folder name");
      return;
    }

    try {
      const newRef = push(ref(db, "elibrary_folders"));
      await set(newRef, {
        name: formData.name,
        description: formData.description || "",
        icon: formData.icon,
        createdAt: new Date().toISOString(),
        createdBy: userName,
        resourceCount: 0
      });
      showMessage("success", `Folder "${formData.name}" created successfully!`);
      setShowFolderModal(false);
      setFormData({ name: "", description: "", icon: "📁" });
    } catch (error) {
      showMessage("error", "Failed to create folder");
    }
  };

  const updateFolder = async () => {
    if (!formData.name.trim()) {
      showMessage("error", "Please enter folder name");
      return;
    }

    try {
      await update(ref(db, `elibrary_folders/${editId}`), {
        name: formData.name,
        description: formData.description || "",
        icon: formData.icon,
        updatedAt: new Date().toISOString(),
        updatedBy: userName
      });
      showMessage("success", "Folder updated successfully!");
      setShowFolderModal(false);
      setFormData({ name: "", description: "", icon: "📁" });
      setIsEditing(false);
      setEditId(null);
    } catch (error) {
      showMessage("error", "Failed to update folder");
    }
  };

  const deleteFolder = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete folder "${name}" and all its contents?`)) {
      try {
        // Delete all resources in the folder
        const resourcesRef = ref(db, `elibrary_resources/${id}`);
        await remove(resourcesRef);
        
        // Delete the folder
        await remove(ref(db, `elibrary_folders/${id}`));
        
        if (selectedFolder?.id === id) {
          setSelectedFolder(null);
        }
        showMessage("success", `Folder "${name}" deleted successfully!`);
      } catch (error) {
        showMessage("error", "Failed to delete folder");
      }
    }
  };

  const openEditFolder = (folder) => {
    setFormData({
      name: folder.name,
      description: folder.description || "",
      icon: folder.icon || "📁"
    });
    setIsEditing(true);
    setEditId(folder.id);
    setShowFolderModal(true);
  };

  // Resource CRUD operations
  const addResource = async () => {
    if (!resourceForm.title.trim()) {
      showMessage("error", "Please enter title");
      return;
    }

    if (!resourceForm.url.trim()) {
      showMessage("error", "Please enter URL or Drive link");
      return;
    }

    try {
      const newRef = push(ref(db, `elibrary_resources/${selectedFolder.id}`));
      await set(newRef, {
        title: resourceForm.title,
        description: resourceForm.description || "",
        type: resourceForm.type,
        url: resourceForm.url,
        createdAt: new Date().toISOString(),
        createdBy: userName
      });

      // Update folder resource count
      const folderRef = ref(db, `elibrary_folders/${selectedFolder.id}`);
      await update(folderRef, {
        resourceCount: resources.length + 1
      });

      showMessage("success", "Resource added successfully!");
      setShowResourceModal(false);
      setResourceForm({
        title: "",
        description: "",
        type: "link",
        url: ""
      });
    } catch (error) {
      showMessage("error", "Failed to add resource");
    }
  };

  const deleteResource = async (resourceId, title) => {
    if (window.confirm(`Are you sure you want to delete "${title}"?`)) {
      try {
        await remove(ref(db, `elibrary_resources/${selectedFolder.id}/${resourceId}`));
        
        // Update folder resource count
        const folderRef = ref(db, `elibrary_folders/${selectedFolder.id}`);
        await update(folderRef, {
          resourceCount: resources.length - 1
        });
        
        showMessage("success", "Resource deleted successfully!");
      } catch (error) {
        showMessage("error", "Failed to delete resource");
      }
    }
  };

  const openResource = (resource) => {
    window.open(resource.url, '_blank', 'noopener,noreferrer');
  };

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      localStorage.clear();
      navigate("/");
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loader}></div>
        <p>Loading E-Library...</p>
      </div>
    );
  }

  // Access control
  if (role !== "admin") {
    return (
      <div style={styles.accessDenied}>
        <div style={styles.accessDeniedCard}>
          <span style={styles.accessDeniedIcon}>🔒</span>
          <h2>Access Denied</h2>
          <p>You don't have permission to access this page.</p>
          <button onClick={() => navigate("/admin-dashboard")} style={styles.backBtn}>
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Message Toast */}
      {message.text && (
        <div style={{...styles.toast, ...(message.type === "success" ? styles.toastSuccess : styles.toastError)}}>
          {message.type === "success" ? "✅" : "❌"} {message.text}
        </div>
      )}

      {/* Create Folder Modal */}
      {showFolderModal && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h3>{isEditing ? "✏️ Edit Folder" : "📁 Create New Folder"}</h3>
              <button onClick={() => { setShowFolderModal(false); setIsEditing(false); setFormData({ name: "", description: "", icon: "📁" }); }} style={styles.closeModalBtn}>✕</button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Folder Name *</label>
                <input
                  type="text"
                  placeholder="e.g., NCERT Books, Reference Materials"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Icon (Emoji)</label>
                <input
                  type="text"
                  placeholder="📁"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  style={styles.inputSmall}
                  maxLength="2"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Description (Optional)</label>
                <textarea
                  placeholder="Brief description of this folder..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  style={styles.textarea}
                  rows="3"
                />
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button onClick={isEditing ? updateFolder : createFolder} style={styles.saveBtn}>
                {isEditing ? "✏️ Update" : "➕ Create"}
              </button>
              <button onClick={() => { setShowFolderModal(false); setIsEditing(false); }} style={styles.cancelBtn}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Resource Modal */}
      {showResourceModal && selectedFolder && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h3>➕ Add Resource to "{selectedFolder.name}"</h3>
              <button onClick={() => { setShowResourceModal(false); setResourceForm({ title: "", description: "", type: "link", url: "" }); }} style={styles.closeModalBtn}>✕</button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Title *</label>
                <input
                  type="text"
                  placeholder="Enter resource title"
                  value={resourceForm.title}
                  onChange={(e) => setResourceForm({ ...resourceForm, title: e.target.value })}
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>URL / Drive Link / PDF Link *</label>
                <input
                  type="text"
                  placeholder="https://drive.google.com/... or https://example.com/book.pdf"
                  value={resourceForm.url}
                  onChange={(e) => setResourceForm({ ...resourceForm, url: e.target.value })}
                  style={styles.input}
                />
                <p style={styles.hintText}>
                  💡 Supported links: Google Drive (shareable link), Direct PDF URL, Any website URL
                </p>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Description (Optional)</label>
                <textarea
                  placeholder="Brief description..."
                  value={resourceForm.description}
                  onChange={(e) => setResourceForm({ ...resourceForm, description: e.target.value })}
                  style={styles.textarea}
                  rows="2"
                />
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button onClick={addResource} style={styles.saveBtn}>➕ Add Resource</button>
              <button onClick={() => { setShowResourceModal(false); }} style={styles.cancelBtn}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>📚 E-Library Management</h1>
          <p style={styles.subtitle}>Organize digital resources (Google Drive, PDF links, websites)</p>
        </div>
        <div style={styles.headerRight}>
          <div style={styles.adminProfile} onClick={() => setShowMobileMenu(!showMobileMenu)}>
            <div style={styles.adminAvatar}>{userName.charAt(0).toUpperCase()}</div>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {showMobileMenu && (
        <div style={styles.mobileMenu}>
          <div style={styles.mobileMenuItem} onClick={() => { navigate("/profile"); setShowMobileMenu(false); }}><span>👤</span> My Profile</div>
          <div style={styles.mobileMenuItem} onClick={() => { navigate("/settings"); setShowMobileMenu(false); }}><span>⚙️</span> Settings</div>
          <div style={styles.mobileMenuItem} onClick={() => { handleLogout(); setShowMobileMenu(false); }}><span>🚪</span> Logout</div>
        </div>
      )}

      {/* Create Folder Button */}
      <button onClick={() => setShowFolderModal(true)} style={styles.createFolderBtn}>
        📁 + Create New Folder
      </button>

      {/* Folders Section */}
      <div style={styles.foldersSection}>
        <h2 style={styles.sectionTitle}>📁 Folders</h2>
        <div style={styles.foldersGrid}>
          {folders.map((folder) => (
            <div
              key={folder.id}
              style={{
                ...styles.folderCard,
                ...(selectedFolder?.id === folder.id ? styles.folderCardActive : {})
              }}
              onClick={() => setSelectedFolder(folder)}
            >
              <div style={styles.folderHeader}>
                <span style={styles.folderIcon}>{folder.icon || "📁"}</span>
                <div style={styles.folderActions}>
                  <button onClick={(e) => { e.stopPropagation(); openEditFolder(folder); }} style={styles.editFolderBtn}>✏️</button>
                  <button onClick={(e) => { e.stopPropagation(); deleteFolder(folder.id, folder.name); }} style={styles.deleteFolderBtn}>🗑️</button>
                </div>
              </div>
              <h3 style={styles.folderName}>{folder.name}</h3>
              <p style={styles.folderDesc}>{folder.description || "No description"}</p>
              <div style={styles.folderStats}>
                <span>📄 {folder.resourceCount || 0} resources</span>
                <span>📅 {folder.createdAt ? new Date(folder.createdAt).toLocaleDateString() : "Recently"}</span>
              </div>
            </div>
          ))}
          {folders.length === 0 && (
            <div style={styles.emptyFolders}>
              <span>📁</span>
              <p>No folders yet. Create your first folder!</p>
            </div>
          )}
        </div>
      </div>

      {/* Resources Section */}
      {selectedFolder && (
        <div style={styles.resourcesSection}>
          <div style={styles.resourcesHeader}>
            <h2 style={styles.sectionTitle}>
              📄 {selectedFolder.name} - Resources
            </h2>
            <button onClick={() => setShowResourceModal(true)} style={styles.addResourceBtn}>
              + Add Resource
            </button>
          </div>

          {resources.length === 0 ? (
            <div style={styles.emptyResources}>
              <span>📭</span>
              <p>No resources in this folder. Click "Add Resource" to add links.</p>
            </div>
          ) : (
            <div style={styles.resourcesGrid}>
              {resources.map((resource) => (
                <div key={resource.id} style={styles.resourceCard} onClick={() => openResource(resource)}>
                  <div style={styles.resourceHeader}>
                    <span style={styles.resourceIcon}>🔗</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteResource(resource.id, resource.title); }}
                      style={styles.deleteResourceBtn}
                    >
                      🗑️
                    </button>
                  </div>
                  <h3 style={styles.resourceTitle}>{resource.title}</h3>
                  <p style={styles.resourceDesc}>{resource.description || "No description"}</p>
                  <div style={styles.resourceUrlDisplay}>
                    {resource.url.substring(0, 50)}...
                  </div>
                  <div style={styles.resourceMeta}>
                    <span>📅 {new Date(resource.createdAt).toLocaleDateString()}</span>
                  </div>
                  <div style={styles.resourceBadge}>
                    🔗 Web Link / Document
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Instructions Card */}
      <div style={styles.instructionsCard}>
        <h4 style={styles.instructionsTitle}>💡 How to Add Resources</h4>
        <ul style={styles.instructionsList}>
          <li><strong>Google Drive Links:</strong> Upload PDF to Google Drive → Share → Copy link → Paste here</li>
          <li><strong>Direct PDF URLs:</strong> Get direct PDF link from any website and paste</li>
          <li><strong>Website Links:</strong> Add any educational website URL</li>
          <li><strong>YouTube Videos:</strong> Add YouTube video links for educational content</li>
        </ul>
      </div>

      {/* Bottom Navigation */}
      <div style={styles.bottomNav}>
        {bottomNavItems.map((item) => (
          <div
            key={item.id}
            style={{
              ...styles.bottomNavItem,
              ...(location.pathname === item.path && styles.bottomNavItemActive)
            }}
            onClick={() => navigate(item.path)}
          >
            <div style={styles.bottomNavIcon}>
              {location.pathname === item.path ? item.activeIcon : item.icon}
            </div>
            <span style={styles.bottomNavLabel}>{item.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "clamp(16px, 4vw, 32px)",
    paddingBottom: "80px",
    fontFamily: "'Segoe UI', 'Inter', system-ui, sans-serif",
    backgroundColor: "#f7fafc",
    minHeight: "100vh",
    overflowY: "auto",  // Add this
    WebkitOverflowScrolling: "touch",  // Add this
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    backgroundColor: "#f7fafc",
  },
  loader: {
    width: "50px",
    height: "50px",
    border: "3px solid #e2e8f0",
    borderTopColor: "#667eea",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  toast: {
    position: "fixed",
    top: "20px",
    right: "20px",
    padding: "12px 20px",
    borderRadius: "8px",
    zIndex: 2000,
    animation: "slideIn 0.3s ease",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
  toastSuccess: { backgroundColor: "#48bb78", color: "#fff" },
  toastError: { backgroundColor: "#e53e3e", color: "#fff" },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: "16px",
    marginBottom: "24px",
  },
  title: {
    fontSize: "clamp(24px, 5vw, 32px)",
    fontWeight: "700",
    margin: "0 0 8px 0",
    color: "#2d3748",
  },
  subtitle: {
    fontSize: "14px",
    color: "#718096",
    margin: 0,
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  adminProfile: { cursor: "pointer" },
  adminAvatar: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #667eea, #764ba2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontWeight: "bold",
    fontSize: "18px",
  },
  mobileMenu: {
    position: "absolute",
    top: "70px",
    right: "20px",
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    zIndex: 1000,
    minWidth: "160px",
    overflow: "hidden",
  },
  mobileMenuItem: {
    padding: "12px 16px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    fontSize: "14px",
    color: "#2d3748",
    cursor: "pointer",
    transition: "background 0.2s ease",
    borderBottom: "1px solid #e2e8f0",
  },
  createFolderBtn: {
    width: "100%",
    padding: "14px",
    backgroundColor: "#667eea",
    color: "#fff",
    border: "none",
    borderRadius: "12px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    marginBottom: "32px",
    transition: "all 0.3s ease",
  },
  foldersSection: {
    marginBottom: "32px",
  },
  sectionTitle: {
    fontSize: "20px",
    fontWeight: "600",
    marginBottom: "16px",
    color: "#2d3748",
  },
  foldersGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: "20px",
  },
  folderCard: {
    backgroundColor: "#fff",
    borderRadius: "16px",
    padding: "20px",
    cursor: "pointer",
    transition: "all 0.3s ease",
    border: "2px solid #e2e8f0",
  },
  folderCardActive: {
    borderColor: "#667eea",
    backgroundColor: "#f0f4ff",
  },
  folderHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
  },
  folderIcon: { fontSize: "40px" },
  folderActions: { display: "flex", gap: "8px" },
  editFolderBtn: { background: "none", border: "none", fontSize: "16px", cursor: "pointer", padding: "4px 8px", borderRadius: "6px" },
  deleteFolderBtn: { background: "none", border: "none", fontSize: "16px", cursor: "pointer", padding: "4px 8px", borderRadius: "6px" },
  folderName: { fontSize: "18px", fontWeight: "600", margin: "0 0 8px 0", color: "#2d3748" },
  folderDesc: { fontSize: "13px", color: "#718096", margin: "0 0 12px 0", lineHeight: "1.4" },
  folderStats: { display: "flex", justifyContent: "space-between", fontSize: "11px", color: "#a0aec0", marginTop: "8px" },
  emptyFolders: { textAlign: "center", padding: "60px", backgroundColor: "#fff", borderRadius: "16px", color: "#a0aec0" },
  resourcesSection: {
    marginTop: "32px",
    paddingTop: "24px",
    borderTop: "2px solid #e2e8f0",
  },
  resourcesHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    flexWrap: "wrap",
    gap: "12px",
  },
  addResourceBtn: {
    padding: "10px 20px",
    backgroundColor: "#48bb78",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },
  resourcesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: "16px",
  },
  resourceCard: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "16px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    border: "1px solid #e2e8f0",
  },
  resourceHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
  },
  resourceIcon: { fontSize: "32px" },
  deleteResourceBtn: { background: "none", border: "none", fontSize: "18px", cursor: "pointer", padding: "4px 8px", borderRadius: "6px" },
  resourceTitle: { fontSize: "16px", fontWeight: "600", margin: "0 0 8px 0", color: "#2d3748" },
  resourceDesc: { fontSize: "12px", color: "#718096", margin: "0 0 8px 0", lineHeight: "1.4" },
  resourceUrlDisplay: { fontSize: "10px", color: "#a0aec0", marginBottom: "8px", wordBreak: "break-all" },
  resourceMeta: { display: "flex", justifyContent: "space-between", fontSize: "10px", color: "#a0aec0", marginBottom: "8px", flexWrap: "wrap", gap: "4px" },
  resourceBadge: { display: "inline-block", fontSize: "10px", padding: "3px 8px", borderRadius: "12px", backgroundColor: "#edf2f7", color: "#4a5568" },
  emptyResources: { textAlign: "center", padding: "60px", backgroundColor: "#fff", borderRadius: "16px", color: "#a0aec0" },
  instructionsCard: { backgroundColor: "#edf2f7", borderRadius: "12px", padding: "16px 20px", marginTop: "20px" },
  instructionsTitle: { fontSize: "13px", fontWeight: "600", margin: "0 0 10px 0", color: "#4a5568" },
  instructionsList: { margin: 0, paddingLeft: "20px", fontSize: "12px", color: "#4a5568", lineHeight: "1.6" },
  hintText: { fontSize: "11px", color: "#a0aec0", marginTop: "4px" },
  modal: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.8)",
    zIndex: 2000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalContent: {
    backgroundColor: "#fff",
    borderRadius: "16px",
    width: "90%",
    maxWidth: "500px",
    maxHeight: "85vh",
    overflow: "auto",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 20px",
    borderBottom: "1px solid #e2e8f0",
  },
  closeModalBtn: { background: "none", border: "none", fontSize: "24px", cursor: "pointer", padding: "0 8px" },
  modalBody: { padding: "20px" },
  formGroup: { marginBottom: "16px" },
  label: { display: "block", fontSize: "14px", fontWeight: "500", marginBottom: "8px", color: "#4a5568" },
  input: { width: "100%", padding: "10px 12px", borderRadius: "8px", border: "2px solid #e2e8f0", fontSize: "14px", boxSizing: "border-box" },
  inputSmall: { width: "80px", padding: "10px 12px", borderRadius: "8px", border: "2px solid #e2e8f0", fontSize: "14px", textAlign: "center" },
  textarea: { width: "100%", padding: "10px 12px", borderRadius: "8px", border: "2px solid #e2e8f0", fontSize: "14px", fontFamily: "inherit", resize: "vertical" },
  modalFooter: { display: "flex", gap: "12px", padding: "16px 20px", borderTop: "1px solid #e2e8f0" },
  saveBtn: { flex: 1, padding: "10px", backgroundColor: "#48bb78", color: "#fff", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: "600", cursor: "pointer" },
  cancelBtn: { flex: 1, padding: "10px", backgroundColor: "#e2e8f0", color: "#4a5568", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: "600", cursor: "pointer" },
  bottomNav: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "#fff",
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    padding: "8px 8px",
    boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
    zIndex: 100,
    borderTop: "1px solid #e2e8f0",
  },
  bottomNavItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "4px",
    cursor: "pointer",
    padding: "6px 8px",
    borderRadius: "8px",
    transition: "all 0.2s ease",
    flex: 1,
  },
  bottomNavItemActive: { color: "#667eea" },
  bottomNavIcon: { fontSize: "20px" },
  bottomNavLabel: { fontSize: "10px", fontWeight: "500" },
  accessDenied: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  },
  accessDeniedCard: {
    textAlign: "center",
    padding: "40px",
    background: "#fff",
    borderRadius: "16px",
    maxWidth: "400px",
    margin: "20px",
  },
  accessDeniedIcon: { fontSize: "64px", display: "block", marginBottom: "16px" },
  backBtn: { marginTop: "20px", padding: "12px 24px", background: "#667eea", color: "#fff", border: "none", borderRadius: "8px", fontSize: "14px", cursor: "pointer" },
};

// Add animations
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
  .folder-card:hover, .resource-card:hover { transform: translateY(-4px); box-shadow: 0 8px 16px rgba(0,0,0,0.1); }
  .edit-folder-btn:hover { background: #e2e8f0; }
  .delete-folder-btn:hover, .delete-resource-btn:hover { background: #fed7d7; }
  @media (max-width: 768px) {
    .folders-grid, .resources-grid { grid-template-columns: 1fr; }
  }
`;
document.head.appendChild(styleSheet);